import * as React from 'react';
import { useFormState } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { Select, Props as PropsSelect } from '../Select/Select';
import MenuItem from '@mui/material/MenuItem';
import { ValidationRuleFragment } from '@websky/graphql';
import { concatPassengerNames } from '../../../../utils';
import { useTheme } from '../../../../theme';
import { isBefore, parse, parseISO, isEqual } from 'date-fns';

interface OwnProps {
	validations: ValidationRuleFragment[];
}

type PropsSelectType = Omit<PropsSelect, 'options'>;

export const LinkedTraveller: React.FC<PropsSelectType & OwnProps> = props => {
	const { t } = useTranslation('Passenger');
	const { LinkedTraveller: theme, Select: selectTheme } = useTheme('Passenger');
	const formState = useFormState();

	const minDateAsStr = props.validations[0].minDate;

	const minDate = minDateAsStr ? parseISO(minDateAsStr) : null;

	const options = props.validations[0].options
		.filter(opt => {
			const passenger = formState.values.passengers[opt.value];
			if (!passenger?.birthDate || !minDate) {
				return true;
			}

			const passengerBirthday = parse(passenger.birthDate, 'dd.MM.yyyy', new Date());

			return isBefore(passengerBirthday, minDate) || isEqual(passengerBirthday, minDate);
		})
		.map(option => {
			return {
				key: option.label,
				value: option.value
			};
		});

	const valueRenderer = React.useCallback(
		value => {
			const passenger = formState.values.passengers[value];
			let name = `${t('Adult')} ${parseInt(value, 10) + 1}`;

			if (passenger?.firstName?.trim() && passenger?.lastName?.trim()) {
				name = concatPassengerNames({
					lastName: passenger.lastName,
					firstName: passenger.firstName,
					middleName: passenger.middleName
				});
			}

			return name;
		},
		[props.value, formState.values.passengers]
	);

	return (
		<Select
			{...props}
			classes={{
				...props.classes,
				input_error: cn(theme.inputError, props.classes?.input_error)
			}}
			renderValue={valueRenderer}
			renderOption={option => {
				return (
					<MenuItem key={option.key} value={option.key} className={selectTheme.option}>
						{valueRenderer(option.value)}
					</MenuItem>
				);
			}}
			readonly={options.length === 1 && props.value === options[0].value}
			value={props.value}
			options={options}
			name={props.name}
			label={props.label}
			className={props.className}
			onChange={props.onChange}
			onBlur={props.onBlur}
			error={props.error}
			disabled={props.disabled}
			selectFirstOptionByDefault
		/>
	);
};
